import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import SEO from '../components/SEO';
import RelativeTime from '../components/RelativeTime';
import './CategoryPages.css';

function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fuzzy search function
  const fuzzySearch = (articles, searchQuery) => {
    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/);
    
    return articles.filter(article => {
      const searchText = (
        (article.title || '') + ' ' +
        (article.subtitle || '') + ' ' +
        (article.content || '') + ' ' +
        (article.category || '') + ' ' +
        (article.author || '') + ' ' +
        (article.authorByline || '')
      ).toLowerCase();
      
      // Check for exact matches first
      if (searchText.includes(query)) return true;
      
      // Check if most words match (fuzzy matching)
      const matchedWords = queryWords.filter(word => {
        if (word.length <= 2) return searchText.includes(word);
        
        // Allow 1 character difference for words 3-5 chars
        if (word.length <= 5) {
          return searchText.includes(word) || 
                 searchText.includes(word.slice(0, -1)) || // missing last char
                 searchText.includes(word.slice(1)) || // missing first char
                 searchText.split(' ').some(textWord => 
                   Math.abs(textWord.length - word.length) <= 1 &&
                   textWord.substring(0, word.length - 1) === word.substring(0, word.length - 1)
                 );
        }
        
        // Allow 2 character differences for longer words
        return searchText.includes(word) ||
               searchText.includes(word.slice(0, -1)) ||
               searchText.includes(word.slice(1)) ||
               searchText.includes(word.slice(0, -2)) ||
               searchText.split(' ').some(textWord => {
                 if (Math.abs(textWord.length - word.length) > 2) return false;
                 let differences = 0;
                 const minLength = Math.min(textWord.length, word.length);
                 for (let i = 0; i < minLength; i++) {
                   if (textWord[i] !== word[i]) differences++;
                   if (differences > 2) return false;
                 }
                 return differences <= 2;
               });
      });
      
      // Return true if at least 70% of words match
      return matchedWords.length >= Math.ceil(queryWords.length * 0.7);
    });
  };

  useEffect(() => {
    const performSearch = async () => {
      if (!query) return;
      
      try {
        setLoading(true);
        const response = await articlesAPI.searchArticles(query);
        let articles = response.posts || response || [];
        
        // If no results from API, try fuzzy search on all articles
        if (articles.length === 0) {
          const allArticlesResponse = await articlesAPI.getArticles();
          const allArticles = allArticlesResponse.posts || allArticlesResponse || [];
          articles = fuzzySearch(allArticles, query);
        }
        
        setSearchResults(articles);
        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query]);

  if (loading) return <div className="loading">Searching...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <main className="category-page" style={{
      '--text-primary': 'var(--text-color, #000)',
      '--text-secondary': 'var(--text-secondary-color, #666)'
    }}>
      <SEO 
        title={`Search Results for "${query}"`}
        description={`Search results for "${query}" on E Media Forum - Entertainment industry news and updates.`}
      />
      
      <div className="category-header">
        <h1 className="category-title">Search Results</h1>
        <p className="category-subtitle">{searchResults.length} articles found for "{query}"</p>
      </div>

      {searchResults.length === 0 ? (
        <div className="no-results">
          <p>No articles found for "{query}". Try different keywords.</p>
        </div>
      ) : (
        <div style={{marginTop: '20px'}}>
          {searchResults.map(article => (
            <div key={article.id} style={{
              display: 'grid',
              gridTemplateColumns: '380px 1fr',
              gap: '24px',
              padding: '20px 0',
              borderBottom: '1px solid #e5e5e5',
              marginBottom: '5px'
            }}>
              <Link to={`/article/${article.id}`}>
                <img 
                  src={article.image || 'https://via.placeholder.com/380x210'} 
                  alt={article.title || 'Article image'}
                  style={{
                    width: '100%',
                    height: '210px',
                    objectFit: 'cover'
                  }}
                />
              </Link>
              <div style={{
                padding: '0',
                height: '210px',
                overflow: 'hidden'
              }}>
                <div>
                  <Link to={`/article/${article.id}`} style={{textDecoration: 'none', color: 'inherit'}}>
                    <h3 style={{
                      fontSize: '28px',
                      fontWeight: '900',
                      color: 'var(--text-primary, #000)',
                      margin: '0 0 10px 0',
                      lineHeight: '1.2',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {article.title || 'Untitled Article'}
                    </h3>
                  </Link>
                  <p style={{
                    fontSize: '16px',
                    color: '#666',
                    margin: '0 0 15px 0',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {article.subtitle || 
                     (article.excerpt ? article.excerpt.substring(0, 150) + '...' : '') ||
                     (article.content ? article.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' : 'No description available')}
                  </p>
                </div>
                <div style={{marginTop: 'auto'}}>
                  <div className="search-author-name" style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px'
                  }}>
                    BY {(article.author || 'STAFF').toUpperCase()}
                  </div>
                  <RelativeTime 
                    date={article.createdAt || article.publishTime || article.fullDate || article.date} 
                    className="" 
                    style={{
                      fontSize: '12px',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

export default SearchPage;